import { useState } from "react";
import { AddressAutocompleteInput } from "@/components/common/AddressAutocompleteInput";
import { saveCoverageCampaign } from "@/lib/coverage/coverageClient";
import type { CoverageCampaign } from "@/lib/coverage/coverageProblems";

type Props = {
  campaigns: CoverageCampaign[];
  polygon: unknown | null;
  drawing: boolean;
  showCampaigns: boolean;
  onShowCampaigns: (next: boolean) => void;
  onDraw: () => void;
  onSaved: () => void;
};

const field = "mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100";

export function CoverageCampaignEditor(props: Props) {
  const [name, setName] = useState("");
  const [platform, setPlatform] = useState<"google" | "facebook">("google");
  const [spend, setSpend] = useState("");
  const [startsOn, setStartsOn] = useState("");
  const [endsOn, setEndsOn] = useState("");
  const [address, setAddress] = useState("");
  const [miles, setMiles] = useState("");
  const [active, setActive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setBusy(true);
    setError(null);
    const res = await saveCoverageCampaign({
      name,
      platform,
      spend_usd: spend,
      starts_on: startsOn,
      ends_on: endsOn,
      address,
      radius_miles: miles,
      polygon: props.polygon,
      active,
    });
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setName("");
    setSpend("");
    setAddress("");
    setMiles("");
    props.onSaved();
  }

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Campaigns</h3>
        <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-zinc-300">
          <input type="checkbox" checked={props.showCampaigns} onChange={(e) => props.onShowCampaigns(e.target.checked)} />
          Show on map
        </label>
      </div>
      <ul className="mt-2 max-h-28 space-y-1 overflow-auto text-sm">
        {props.campaigns.map((campaign) => (
          <li key={campaign.id} className="text-gray-800 dark:text-zinc-100">
            {campaign.name} · {campaign.platform} · ${campaign.spendUsd}
            {campaign.active ? "" : " · off"}
          </li>
        ))}
      </ul>
      <div className="mt-3 space-y-2">
        <label className="block text-xs text-gray-500">
          Name
          <input className={field} value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="block text-xs text-gray-500">
          Platform
          <select className={field} value={platform} onChange={(e) => setPlatform(e.target.value === "facebook" ? "facebook" : "google")}>
            <option value="google">Google</option>
            <option value="facebook">Facebook</option>
          </select>
        </label>
        <label className="block text-xs text-gray-500">
          Spend dollars
          <input className={field} value={spend} onChange={(e) => setSpend(e.target.value)} />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="block text-xs text-gray-500">
            Starts
            <input className={field} type="date" value={startsOn} onChange={(e) => setStartsOn(e.target.value)} />
          </label>
          <label className="block text-xs text-gray-500">
            Ends
            <input className={field} type="date" value={endsOn} onChange={(e) => setEndsOn(e.target.value)} />
          </label>
        </div>
        <label className="block text-xs text-gray-500">
          Circle address
          <div className="mt-1">
            <AddressAutocompleteInput value={address} onChange={setAddress} placeholder="Center of the spend" />
          </div>
        </label>
        <label className="block text-xs text-gray-500">
          Radius miles
          <input className={field} value={miles} onChange={(e) => setMiles(e.target.value)} />
        </label>
        <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-zinc-300">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          Running
        </label>
        <div className="flex gap-2">
          <button type="button" className="rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-zinc-600" onClick={props.onDraw}>
            {props.drawing ? "Drawing…" : "Draw campaign"}
          </button>
          <button
            type="button"
            disabled={busy || !name.trim()}
            className="rounded-lg bg-gray-900 px-3 py-2 text-sm text-white disabled:opacity-50 dark:bg-zinc-100 dark:text-zinc-900"
            onClick={() => void save()}
          >
            {busy ? "Saving…" : "Save campaign"}
          </button>
        </div>
        {props.polygon ? <p className="text-xs text-gray-500">A drawn polygon will be saved with this campaign.</p> : null}
        {error ? <p className="text-sm text-red-600">{error}</p> : null}
      </div>
    </section>
  );
}
